import React, { useState, useEffect, useRef } from "react";
import { TextField, Paper, List, ListItemButton, ListItemText, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { mapService } from "../../services/map.service";
import { mapSearchService } from "../../services/map-search.service";

const MapSearchControl: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounce
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 400);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query]);

  // Trigger search
  useEffect(() => {
    if (debouncedQuery.length < 3) {
      setResults([]);
      return;
    }

    const search = async () => {
      const data = await mapSearchService.searchAddress(debouncedQuery);
      setResults(data);
    };

    search();
  }, [debouncedQuery]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (result: any) => {
    const map = mapService.getMapInstance();

    map?.setCamera({
      center: [result.position.lon, result.position.lat],
      zoom: 16,
      duration: 1000
    });

    setQuery(result.address?.freeformAddress ?? "");
    setResults([]);
    setOpen(false);
  };

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 1000
        }}
      >
        <button
          className="azure-map-button"
          onClick={() => setOpen((prev) => !prev)}
          type="button"
        >
          <SearchIcon fontSize="small" />
        </button>
      </div>
  
      <div
        className="ip-map-search-control"
        ref={containerRef}
        style={{
          transform: open
            ? "translate(-50%, 0)"
            : "translate(-50%, -20px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <TextField
          fullWidth
          variant="filled"
          size="small"
          hiddenLabel
          placeholder="Zoek locatie..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus={open}
          sx={{
            mb: 1,
            "& .MuiFilledInput-root": {
              borderRadius: "4px",
              backgroundColor: "#f5f5f5",
            },
            "& .MuiFilledInput-input": {
              paddingTop: "12px",
              paddingBottom: "12px",
              boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.16)",
            },
            "& .MuiInputAdornment-root": {
              marginTop: "0px",
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }
          }}
        />
  
        {results.length > 0 && (
          <Paper>
            <List dense disablePadding>
              {results.map((r, index) => (
                <ListItemButton
                  key={index}
                  onClick={() => handleSelect(r)}
                >
                  <ListItemText
                    primary={r.address?.freeformAddress ?? "Onbekend"}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </div>
    </>
  );  
};

export default MapSearchControl;
